import React, { useCallback, useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import Router, { useRouter } from 'next/router'
import { functions, path, useApp } from '@wap-client/core'
import { pageService } from '@wap-client/services'
import { Row, Column, Container } from '@/components/base/gridview'
import Icon from '@/components/base/icon'
import Anchor from '@/components/base/anchor'
import Image from '@/components/base/image'
import Button from '@/components/base/button'
import LoginRegister from '@/components/widgets/header/LoginRegister'
import WhatsonCTAButton from '@/components/widgets/header/WhatsonCTAButton'
import HospitalityCTAButton from '@/components/widgets/header/HospitalityCTAButton'
import Search from './Search'
import Navigation from './Navigation'
import { HeaderProps } from './types'
import useAuthStore from '@/context/useAuth'
import { useMobileRotateDeviceType } from '@/components/hooks/useDeviceType'
import { useDeviceType } from '@/hooks/useDeviceType'
import { Cookie } from '@/utils/functions/cookie'
import clsx from 'clsx'
import useDirectionRtl from '@/hooks/useDirectionRtl'
import useTranslations from '@/hooks/useTranslations'

interface LangOption {
  id: string
  title: string
  url: string
}

const Header: React.FC<HeaderProps> = ({
  hotelsAnchor,
  isBlurred,
  isHideBrand,
  isLight,
  isSearchable,
  isTransparent,
  logo,
  logoDark,
  navigation,
  customHomePage,
  whatsonCtaButton,
  hospitalityCtaButton,
}) => {
  const app = useApp()
  const { translation } = useTranslations()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const { isRtl } = useDirectionRtl()
  const deviceType = useDeviceType()
  const router = useRouter()
  const { authData, logoutUser } = useAuthStore()
  const wasAuth = useRef(authData.isAuth)

  const [panel, setPanel] = useState<string>('')
  const [isScrolled, setIsScrolled] = useState(false)

  // Scroll listener
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 200)
    handleScroll()
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Reset panel on route change
  useEffect(() => {
    const onRouteComplete = () => {
      document.body.style.overflow = ''
      setPanel('')
    }
    Router.events.on('routeChangeComplete', onRouteComplete)
    return () => Router.events.off('routeChangeComplete', onRouteComplete)
  }, [])

  // Prevent logout loop
  useEffect(() => {
    if (wasAuth.current && !authData.isAuth) logoutUser()
    wasAuth.current = authData.isAuth
  }, [authData.isAuth, logoutUser])

  // Determine home URL
  const changeUrl = useCallback(() => {
    if (customHomePage?.href) return customHomePage.href
    // if (app.language === 'tr-tr') return `${app.language}/anasayfa`
    // return app.language === 'en-us' ? 'homepage' : `${app.language}/homepage`

    return app.settings.homepage
  }, [app.language, customHomePage])

  const handleLanguageSelection = (language: string) => {
    pageService
      .findPath(app.environment, { path: app.page.path, language })
      .then((slug) => {
        const {
          query: { pathname, ...rest },
        } = router

        router.push({ pathname: path.app(app.environment, slug), query: rest })
      })
      .catch(() => {
        const homepagePath = app.settings.homepage

        if (homepagePath) {
          pageService
            .findPath(app.environment, { path: homepagePath, language })
            .then((slug) => {
              router.push(path.app(app.environment, slug))
            })
        }
      })
    setIsDropdownOpen(false)
  }

  const openLanguage = () => {
    setIsDropdownOpen(!isDropdownOpen)
  }

  const renderLanguageSelection = () => {
    if (app.languages.length > 1) {
      return (
        <div className="header-language-selection">
          <button
            onClick={openLanguage}
            className="header-language-selection-current-btn"
          >
            <Icon name="world" className="prefix-icon" />
            <span className="lang-code">
              {app.language.split('-')[0].toUpperCase()}
            </span>
            <Icon className="icon" name="arrow-down" size="small" />
          </button>

          {isDropdownOpen && (
            <div className="header-language-selection-dropdown">
              <div className="header-language-selection-dropdown-list">
                {app.languages.map((language) => (
                  <li key={language.id}>
                    <Button
                      onClick={() => handleLanguageSelection(language.id)}
                    >
                      {language.title}
                    </Button>
                  </li>
                ))}
              </div>
            </div>
          )}
        </div>
      )
    }
  }

  const linkGoTurkiye = () => {
    const lang = Cookie.get('language')
    if (lang) {
      return `https://goturkiye.com/${lang}`
    } else {
      return 'https://goturkiye.com'
    }
  }

  return (
    <header
      className={functions.classnames(
        'header',
        isBlurred && 'header--blurred',
        isLight && 'header--light',
        isTransparent && 'header--transparent',
        (isScrolled || deviceType === 'mobile') && 'header--scrolled',
        panel && 'header--with-panel',
      )}
    >
      <Container size="extended">
        <Row xs={{ justify: 'between' }}>
          <Column xs={{ auto: true }} style={{ textAlign: 'left' }}>
            <Anchor className="header-logo" href={changeUrl()}>
              <Image
                {...(logoDark?.src && isScrolled && isLight
                  ? logoDark
                  : logo?.src
                    ? logo
                    : app.theme.logo)}
                alt={app.page.title}
                height={50}
                loading="eager"
              />
            </Anchor>
          </Column>
          <Column xs={{ auto: true }} className="header-actions">
            {!useMobileRotateDeviceType(800) && hotelsAnchor?.href && (
              <Anchor className="header-menu-anchor" href={hotelsAnchor.href}>
                <span>{hotelsAnchor.children}</span>
              </Anchor>
            )}
            {renderLanguageSelection()}
            <div className="header-horizontal-line" />
            {isSearchable && (
              <button
                className="header-menu-button"
                onClick={() => setPanel('search')}
              >
                <Icon name="search" size="small" />
              </button>
            )}
            <LoginRegister />
            <button
              className="header-menu-button menu"
              onClick={() =>
                setPanel((prev) => (prev === 'menu' ? '' : 'menu'))
              }
            >
              <Icon name="menu" size="small" />
              <span className="header-menu-button-label">
                {translation('menu')}
              </span>
            </button>
            {!isHideBrand && deviceType === 'desktop' && (
              <Link className="header-goturkey-logo" href={linkGoTurkiye()}>
                <Image
                  {...app.theme.logo}
                  alt={app.settings.shortName}
                  loading="eager"
                />
              </Link>
            )}
          </Column>
        </Row>
      </Container>

      {navigation && (
        <Navigation
          isOpen={panel === 'menu'}
          data={navigation}
          onClose={() => setPanel('')}
          isHideBrand={isHideBrand}
          logo={logo}
        />
      )}

      <Search
        isOpen={panel === 'search'}
        onClose={() => setPanel('')}
        logo={logo}
      />

      {(whatsonCtaButton?.href || hospitalityCtaButton?.href) && (
        <div
          className={clsx('header-cta', {
            'cta-rtl': isRtl,
          })}
        >
          <WhatsonCTAButton whatsonCtaButton={whatsonCtaButton} />
          <HospitalityCTAButton hospitalityCtaButton={hospitalityCtaButton} />
        </div>
      )}
    </header>
  )
}

export default Header
